import React, { useRef, useState, useEffect, FC, useCallback } from 'react';
import HiglightedOption from '../../../../../@shared/highlightedOption';
import { documentService } from '../../../../../../utils/window.utils';
import useSuggestions from './hooks/useSuggestions.effect';
import { iconSize } from '../../../../../../constants/icon.constants';
import Icon from '../../../../../@shared/icon';
import { TTextSearchProps } from '../../../../types/dealerLocatorLanding.types';
import DictionaryTerm from '../../../../../@shared/dictionaryTerm';
import googleLogo from '../../../../../../assets/powered-by-google-on-white.png';
import dealerLocatorSearchSvg from '../../../../../../assets/dealer-landing-search.svg';
import useSearchText from '../../../../hooks/useSearchText.effect';
import useSelectedDealer from './hooks/useSelectedDealer.effect';
import useSelectedPlace from './hooks/useSelectedPlace.effect';
import { googleClickDealerLocatorResults } from '../../../../../../utils/analytics';
import { dealerLocatorResultsOpen } from '../../../../../../constants/analytics.constants';

const RESULTS_LENGTH_LIMIT = 8;

export const TextSearch: FC<TTextSearchProps> = ({
  model,
  countryIsoCode,
  label,
  onDealerSelect,
  onGooglePlaceSelect,
  autocompleteService,
  placesService,
  filteredDealerResults,
  country,
}) => {
  const [searchInputValue, setSearchInputValue] = useSearchText('');
  const [searchText, setSearchText] = useState<string>('');
  const [usersInput, setUsersInput] = useState<boolean>(false);
  const suggestionSettings = {
    pageId: model.pageId,
    language: model.siteLanguage,
    countryIsoCode,
    resultsLimit: RESULTS_LENGTH_LIMIT,
  };
  const [suggestions] = useSuggestions(searchText, searchInputValue, autocompleteService, suggestionSettings);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [higlightedSuggestionIndex, setHiglightedSuggestionIndex] = useState<number | null>(null);
  const [selectedResult, setSelectedResult] = useState<any>(null);
  const textSearchElmRef = useRef<HTMLDivElement>(null);
  const texInputElmRef = useRef<HTMLInputElement>(null);
  const dealerSuggestionsIndexOffset = suggestions?.googlePlacesPredictions?.length ?? 0;
  const [selectedDealer, selectedDealerNumber, setSelectedDealerNumber] = useSelectedDealer(model.pageId, model.siteLanguage);
  const [selectedPlaceId, setSelectedPlaceId] = useSelectedPlace();
  const [shouldUpdateUrl, setShouldUpdateUrl] = useState<boolean>(false);

  const fetchPlaceDetail = useCallback((placeId: string) => {
    if (!placeId) {
      return;
    }

    const placeRequest = {
      placeId: placeId,
      fields: ['geometry'],
    };

    return new Promise<google.maps.places.PlaceResult | null>((resolve, reject) => {
      placesService.getDetails(placeRequest, (result, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          resolve(result);
        } else {
          reject('Failed to fetch place detail from Google Places API');
        }
      });
    });
  }, []);

  const closeDropdown = (): void => {
    setDropdownOpen(false);
  };

  const getDisplayNameFromResult = (result: any): string => {
    if (result?.dealership) {
      return result?.dealership?.dealerName || '';
    }

    return result?.description || '';
  };

  const textChangeHandler = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newSearchText = e.target?.value || '';
    setUsersInput(true);
    setSearchInputValue(newSearchText);
  };

  const inputClickHandler = (): void => {
    setDropdownOpen(true);
    texInputElmRef?.current?.focus();
  };

  const handleSuggestionMouseEnter = (index: number): void => {
    setHiglightedSuggestionIndex(index);
  };

  const getCountryName = (isoCode, countries) => countries?.find((country) => country?.IsoCode === isoCode)?.Country;

  const determineSelectedCountry = (model, isoCode) =>
    model?.searchType === 'SearchByLocationOrDealer' ? getCountryName(isoCode, model?.countries) || isoCode : country?.Country;
  const selectedCountry = determineSelectedCountry(model, countryIsoCode);

  const getSelectedDealerRegionSearch = (result) => {
    return result?.description || result?.dealership?.dealerName || '';
  };

  const { dealerLocatorEquipmentsResults, dealerLocatorServicesResults } = filteredDealerResults || {};

  const handleSuggestionSelect = (result: any): void => {
    if (!result?.dealership) {
      setSelectedPlaceId(result?.place_id);
      setSelectedDealerNumber('');
    } else {
      setSelectedDealerNumber(result?.dealership?.dealerNumber);
      setSelectedPlaceId('');
    }
    setShouldUpdateUrl(true);
    setSelectedResult(result);
    setUsersInput(false);
    texInputElmRef?.current?.blur();

    if ((country?.Country || countryIsoCode) && result) {
      googleClickDealerLocatorResults(
        dealerLocatorResultsOpen,
        selectedCountry,
        getSelectedDealerRegionSearch(result),
        dealerLocatorEquipmentsResults,
        dealerLocatorServicesResults,
        model?.searchType,
        undefined
      );
    }
  };

  const handleClickOutside = (e) => {
    if (textSearchElmRef?.current?.contains(e?.target)) {
      return;
    }

    setUsersInput(false);
    closeDropdown();
  };

  const textInputFocusHandler = () => {
    setDropdownOpen(true);
  };

  useEffect(() => {
    if (usersInput) {
      setSearchText(searchInputValue);
      setHiglightedSuggestionIndex(null);
    }
  }, [searchInputValue, usersInput]);

  useEffect(() => {
    const text = getDisplayNameFromResult(selectedResult);

    if (text) {
      setSearchInputValue(text);
    }
  }, [dropdownOpen, selectedResult]);

  useEffect(() => {
    const doc = documentService() as Document;

    if (dropdownOpen) {
      doc.addEventListener('click', handleClickOutside, false);
    }

    return (): void => {
      doc.removeEventListener('click', handleClickOutside, false);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    if (selectedResult) {
      setUsersInput(false);
      closeDropdown();

      if (selectedResult.dealership) {
        onDealerSelect({
          results: selectedResult,
          queryText: selectedResult?.dealership?.dealerName ?? '',
          selectedDealerId: selectedResult?.dealership?.dealerNumber ?? '',
          updateUrl: shouldUpdateUrl,
        });
        setShouldUpdateUrl(false);
      } else {
        fetchPlaceDetail(selectedResult.place_id)
          ?.then((result) => {
            onGooglePlaceSelect(result, selectedResult?.place_id ?? '', selectedResult?.description ?? '', shouldUpdateUrl);
            setShouldUpdateUrl(false);
          })
          .catch((error) => {
            console.error(error, 'Error while fetching Place Detail');
          });
      }
    }
  }, [selectedResult, fetchPlaceDetail]);

  useEffect(() => {
    setUsersInput(false);
    setSelectedResult(null);
    setSelectedPlaceId('');
    setSelectedDealerNumber('');
  }, [countryIsoCode]);

  useEffect(() => {
    if (searchInputValue === searchText) {
      return;
    }

    setSearchInputValue(searchText);
  }, [searchText]);

  useEffect(() => {
    if (selectedPlaceId && selectedPlaceId !== selectedResult?.place_id) {
      setSelectedResult({ place_id: selectedPlaceId, description: searchInputValue ?? '' });
    }

    if (selectedDealer && selectedDealerNumber !== selectedResult?.dealership?.dealerNumber) {
      setSelectedResult(selectedDealer);
    }
  }, [selectedPlaceId, selectedDealer, selectedResult]);

  const handleEnter = () => {
    if (!suggestions) {
      return;
    }
    if (higlightedSuggestionIndex === null) {
      if (suggestions?.googlePlacesPredictions?.length > 0) {
        handleSuggestionSelect(suggestions?.googlePlacesPredictions[0]);
      } else if (suggestions?.dealershipResults?.length > 0) {
        handleSuggestionSelect(suggestions?.dealershipResults[0]);
      }
    } else {
      const predictionsCount = suggestions?.googlePlacesPredictions?.length || 0;

      if (higlightedSuggestionIndex <= predictionsCount - 1) {
        handleSuggestionSelect(suggestions?.googlePlacesPredictions[higlightedSuggestionIndex]);
      } else {
        handleSuggestionSelect(suggestions?.dealershipResults[higlightedSuggestionIndex - predictionsCount]);
      }
    }
  };

  const handleArrowUp = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!suggestions) {
      return;
    }

    e.preventDefault();
    const resultsLength = suggestions?.dealershipResults.length + suggestions?.googlePlacesPredictions.length;
    const index = higlightedSuggestionIndex !== null && higlightedSuggestionIndex > 0 ? higlightedSuggestionIndex - 1 : resultsLength - 1;
    setHiglightedSuggestionIndex(index);
  };

  const handleArrowDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!suggestions) {
      return;
    }

    e.preventDefault();
    const resultsLength = suggestions?.dealershipResults.length + suggestions?.googlePlacesPredictions.length;
    const index = higlightedSuggestionIndex !== null && higlightedSuggestionIndex < resultsLength - 1 ? higlightedSuggestionIndex + 1 : 0;
    setHiglightedSuggestionIndex(index);
  };

  const keyDownHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleEnter();
      return;
    }

    if (e.key === 'ArrowDown') {
      handleArrowDown(e);
      return;
    }

    if (e.key === 'ArrowUp') {
      handleArrowUp(e);
    }
  };

  const clearSearch = () => {
    setSearchInputValue('');
    setUsersInput(false);
    setSelectedResult(null);
    texInputElmRef?.current?.blur();
  };

  const dealerLocatorResultsClickHandler = useCallback(() => {
    const isSelectedResults = getSelectedDealerRegionSearch(selectedResult);
    if ((country?.Country || countryIsoCode) && isSelectedResults) {
      googleClickDealerLocatorResults(
        dealerLocatorResultsOpen,
        selectedCountry,
        isSelectedResults,
        dealerLocatorEquipmentsResults,
        dealerLocatorServicesResults,
        model?.searchType,
        undefined
      );
    }
  }, [filteredDealerResults]);

  useEffect(() => {
    dealerLocatorResultsClickHandler();
  }, [dealerLocatorResultsClickHandler]);

  return (
    <div className="dealer-locator-landing-search__text-search" ref={textSearchElmRef}>
      <div className="dealer-locator-landing-search__text-search-input-wrapper" onClick={inputClickHandler}>
        <Icon
          iconField={{ iconUrl: dealerLocatorSearchSvg }}
          extraClassName="dealer-locator-landing-search__text-search-input-icon"
          size={iconSize['dsk']}
          color={'gray'}
        />
        <label
          htmlFor="search-input-with-suggestions-input"
          className={`dealer-locator-landing-search__text-search-label ${
            dropdownOpen || searchInputValue ? '' : 'dealer-locator-landing-search__text-search-label--center'
          } `}
        >
          {label}
        </label>
        <input
          type="text"
          id="search-input-with-suggestions-input"
          className="dealer-locator-landing-search__text-search-input"
          value={searchInputValue}
          onChange={textChangeHandler}
          onFocus={textInputFocusHandler}
          onKeyDown={keyDownHandler}
          ref={texInputElmRef}
        />
        {searchInputValue && (
          <Icon
            iconName="action-close"
            size={iconSize.dsk}
            extraClassName="dealer-locator-landing-search__text-search-clear-icon"
            onClick={clearSearch}
          />
        )}
      </div>
      {dropdownOpen && suggestions && (
        <div className="dealer-locator-landing-search__text-search-suggestions">
          {suggestions?.googlePlacesPredictions?.length > 0 && (
            <p className="dealer-locator-landing-search__text-search-suggestion-group-title">
              <DictionaryTerm keyword={'cnhi-dealerLocatorLocation'} />
            </p>
          )}
          <ul className="dealer-locator-landing-search__text-search-suggestion-group">
            {suggestions?.googlePlacesPredictions?.map((result, i) => (
              <li
                className={`dealer-locator-landing-search__text-search-suggestions-list-item ${
                  higlightedSuggestionIndex === i ? 'dealer-locator-landing-search__text-search-suggestions-list-item--highligted' : ''
                }`}
                onMouseEnter={() => handleSuggestionMouseEnter(i)}
                onClick={() => handleSuggestionSelect(result)}
                key={result.place_id}
              >
                <HiglightedOption keyword={searchText} optionText={getDisplayNameFromResult(result)} />
              </li>
            ))}
          </ul>
          {suggestions?.dealershipResults?.length > 0 && (
            <p className="dealer-locator-landing-search__text-search-suggestion-group-title">
              <DictionaryTerm keyword={'cnhi-dealerLocatorDealerName'} />
            </p>
          )}
          <ul className="dealer-locator-landing-search__text-search-suggestion-group">
            {suggestions?.dealershipResults?.map((result, i) => (
              <li
                className={`dealer-locator-landing-search__text-search-suggestions-list-item ${
                  higlightedSuggestionIndex === dealerSuggestionsIndexOffset + i
                    ? 'dealer-locator-landing-search__text-search-suggestions-list-item--highligted'
                    : ''
                }`}
                onMouseEnter={() => handleSuggestionMouseEnter(dealerSuggestionsIndexOffset + i)}
                onClick={() => handleSuggestionSelect(result)}
                key={`${i + dealerSuggestionsIndexOffset}-${result.dealership.dealerNumber}`}
              >
                <HiglightedOption keyword={searchText} optionText={getDisplayNameFromResult(result)} />
                <span className="dealer-locator-landing-search__text-search-suggestion-list-item-address">
                  {result?.dealership?.shippingAddress1}, {result?.dealership?.shippingCity}, {result?.dealership?.shippingZip}
                </span>
              </li>
            ))}
          </ul>
          <img className="dealer-locator-landing-search__text-search-google-logo" src={googleLogo} alt="powered by Google" />
        </div>
      )}
    </div>
  );
};

export default TextSearch;
